/**
 * 通知投递接线（任务 18c2）：会话行回流 → 系统通知。
 *
 * 判定全在 `~/push/notifyDelivery.scanArrivals`（批口径）+ `~/push/notifyDecision`
 * （单条九档过滤，18b），本文件只做三件事：订阅 rows、拼 `NotifyContext`、调
 * 浏览器 API。**零 HTTP**——通知内容全取自已回流的会话行（docs/push.md §7）。
 *
 * 接线放 store 层而不是组件里（同 appBadge）：收不收通知与 UI 挂没挂载无关，
 * 用户停在设置页/通讯录页时照样要弹。
 *
 * 展示通道两条，优先 SW 注册：
 * - `registration.showNotification`：iOS Web Clip **只认这条**（standalone 下
 *   `new Notification()` 根本不可用），安卓 Chrome 同理。
 * - `new Notification()` 兜底：桌面 Safari/Firefox 以及 dev（SW 未注册）下可用。
 *   这条能拿到 `onclick`，直接站内跳会话（SW 那条的点击走 `notificationclick`
 *   → postMessage，接收端是 `~/push/notificationClicks`，18b 已接）。
 * 两条都失败一律静默：弹不出通知不该让页面出错。
 *
 * 第三条通道是**窗口内提示条**（18c2b，桌面前台）：与系统通知互斥，判定在
 * `shouldPopupInApp`，展示交给 `./notifyPopupState` 的控制器（队列 + 超时 + 提示音）。
 *
 * 退出登录（rows 变 null）清空「已见」表：换账号后首批重新 seed，不会把新账号的
 * 存量未读当成新消息弹出来。
 */
import { scanArrivals } from '~/push/notifyDelivery';
import { pushSupportOf, browserPushEnv } from '~/push/pushCapability';
import type { NotifyContent, NotifyContext } from '~/push/notifyDecision';
import { readNotifySettings, type NotifySettings } from '~/shell/notifyModel';
import { activeChatKey } from '~/nav/projection';
import type { NotifyPopupController } from './notifyPopupState';
import type { GlobalState, GlobalStore } from './global';

export interface NotifyDeliveryDeps {
  /** 展示一条系统通知；失败自行吞掉 */
  show(content: NotifyContent): void;
  /**
   * 出一条窗口内提示条（18c2b；桌面前台命中 `foreground` 档时）。
   * 实现在 `./notifyPopupState` 的控制器里（入队 + 超时 + 提示音）。
   */
  showPopup(content: NotifyContent): void;
  /** 清空窗口内提示条（登出/换账号：上一个账号的提示不该留在屏幕上） */
  clearPopups(): void;
  /** 当前通知设置（每次现读：设置页改完立即生效，不用重接线） */
  settings(): NotifySettings;
  /** 通知权限已授权（18a `pushSupportOf === 'ready'`） */
  granted(): boolean;
  /** `document.visibilityState === 'visible'` */
  pageVisible(): boolean;
  /** `document.hasFocus()` */
  windowFocused(): boolean;
}

export function connectNotifyDelivery(store: GlobalStore, deps: NotifyDeliveryDeps): () => void {
  const seen = new Map<string, number>();
  let seeded = false;

  function contextOf(state: GlobalState): NotifyContext {
    return {
      settings: deps.settings(),
      activeKey: activeChatKey(state.nav) ?? null,
      pageVisible: deps.pageVisible(),
      windowFocused: deps.windowFocused(),
      myUid: state.auth.status === 'logged-in' ? state.auth.userId : 0,
      granted: deps.granted(),
      cdnBase: state.config?.cdn ?? '',
      // 断点现读：窗口缩到移动布局后不该再出桌面提示条
      isDesktop: !state.layout.isMobile,
    };
  }

  function onRows(rows: GlobalState['conversations']['rows']): void {
    if (rows === null) {
      // 登出/换账号：下一批重新 seed，屏幕上残留的提示条一并清掉
      // （`seeded` 门控：接线时 rows 本来就是 null，那不是登出，没什么要清）
      seen.clear();
      if (seeded) deps.clearPopups();
      seeded = false;
      return;
    }
    const scan = scanArrivals(rows, seen, contextOf(store.get()), { seed: !seeded });
    seeded = true;
    for (const [key, id] of scan.advanced) seen.set(key, id);
    for (const content of scan.contents) {
      try {
        deps.show(content);
      } catch {
        // 展示失败只丢这一条：异常冒到 store.flush 会连累同帧其它订阅者
      }
    }
    for (const content of scan.popups) {
      try {
        deps.showPopup(content);
      } catch {
        // 同上
      }
    }
  }

  // 首批可能在接线前就已落地（冷启动快照很快）：立刻按 seed 口径种一次
  onRows(store.get().conversations.rows);
  return store.subscribe((s: GlobalState) => s.conversations.rows, onRows);
}

// ── 浏览器侧薄封装（判定全在上面，这里只碰 API） ──────────────────────────

/** `ServiceWorkerRegistration.showNotification` 用到的面 */
interface ShowNotificationHost {
  showNotification(title: string, options?: NotificationOptions): Promise<void>;
}

/**
 * 系统通知展示器。`registration` 是 `swRegistration()` 的 Promise（本页唯一一次
 * 注册，15b）；还没 resolve 或为 null 时走 `new Notification()` 兜底。
 * `onActivate` 只用于兜底通道的点击（SW 通道的点击在 sw/pushEvents，18b）。
 */
export function browserNotifyShow(
  registration: Promise<ServiceWorkerRegistration | null>,
  onActivate: (conversationKey: string) => void,
): (content: NotifyContent) => void {
  let reg: ShowNotificationHost | null = null;
  void registration.then(
    (r) => {
      reg = r;
    },
    () => undefined,
  );

  return (content) => {
    const options: NotificationOptions = {
      body: content.body,
      tag: content.tag,
      data: { url: content.url, conversationKey: content.conversationKey },
      // sound 开关关 → 系统通知静音（18c2b）；开着就不设，交给系统默认行为
      ...(content.silent ? { silent: true } : {}),
      ...(content.icon ? { icon: content.icon } : {}),
    };
    try {
      if (reg) {
        void reg.showNotification(content.title, options).catch(() => undefined);
        return;
      }
      if (typeof Notification !== 'function') return;
      const n = new Notification(content.title, options);
      n.onclick = () => {
        // 兜底通道没有 SW 参与：自己把窗口拉到前台再站内跳
        try {
          window.focus();
        } catch {
          // 某些浏览器禁止脚本 focus：跳转照做
        }
        n.close();
        onActivate(content.conversationKey);
      };
    } catch {
      // 不支持构造 Notification（安卓 Chrome / standalone）且 SW 还没就绪：静默丢弃
    }
  };
}

/** 运行时 deps（settings/权限现读，保证设置页改完下一条就生效） */
export function browserNotifyDeliveryDeps(
  registration: Promise<ServiceWorkerRegistration | null>,
  onActivate: (conversationKey: string) => void,
  isStandalone: () => boolean,
  popups: Pick<NotifyPopupController, 'show' | 'clear'>,
): NotifyDeliveryDeps {
  return {
    show: browserNotifyShow(registration, onActivate),
    showPopup: (content) => popups.show(content),
    clearPopups: () => popups.clear(),
    settings: () => readNotifySettings(localStorage),
    granted: () => pushSupportOf(browserPushEnv(isStandalone())) === 'ready',
    pageVisible: () => document.visibilityState === 'visible',
    windowFocused: () => document.hasFocus(),
  };
}
